import "./core-ux-design.css"
import { useEffect, useRef, useState, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import BlogsSlider from "../../../components/blogs-slider/BlogsSlider"

function CoreUxDesign() {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [activeSection, setActiveSection] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const userCenteredSectionRef = useRef(null);
    const claritySectionRef = useRef(null);
    const consistencySectionRef = useRef(null);

    const userCenteredTopicRef = useRef(null);
    const clarityTopicRef = useRef(null);
    const consistencyTopicRef = useRef(null);
    const topicsContainerRef = useRef(null);

    const { ref: userCenteredRef, inView: userCenteredInView } = useInView({ threshold: 0.3 });
    const { ref: clarityRef, inView: clarityInView } = useInView({ threshold: 0.3 });
    const { ref: consistencyRef, inView: consistencyInView } = useInView({ threshold: 0.3 });

    const updateLinePosition = useCallback(() => {
        const topicRefs = [userCenteredTopicRef, clarityTopicRef, consistencyTopicRef];
        const activeTopicRef = topicRefs[activeSection];
        const currentIsMobile = window.innerWidth <= 768;
        setIsMobile(currentIsMobile);

        if (activeTopicRef?.current && topicsContainerRef.current) {
            if (currentIsMobile) {
                const containerRect = topicsContainerRef.current.getBoundingClientRect();
                const topicRect = activeTopicRef.current.getBoundingClientRect();
                const leftPosition = topicRect.left - containerRect.left;
                setScrollProgress(leftPosition);
            } else {
                const containerRect = topicsContainerRef.current.getBoundingClientRect();
                const topicRect = activeTopicRef.current.getBoundingClientRect();
                const topPosition = topicRect.top - containerRect.top;
                setScrollProgress(topPosition);
            }
        }
    }, [activeSection]);

    useEffect(() => {
        if (consistencyInView) {
            setActiveSection(2);
        } else if (clarityInView) {
            setActiveSection(1);
        } else if (userCenteredInView) {
            setActiveSection(0);
        }
    }, [userCenteredInView, clarityInView, consistencyInView]);

    useEffect(() => {
        const rafId = requestAnimationFrame(() => {
            updateLinePosition();
        });

        return () => cancelAnimationFrame(rafId);
    }, [activeSection, updateLinePosition]);

    useEffect(() => {
        const handleResize = () => {
            updateLinePosition();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateLinePosition]);

    useEffect(() => {
        const container = topicsContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (window.innerWidth <= 768) {
                const topicRefs = [userCenteredTopicRef, clarityTopicRef, consistencyTopicRef];
                const activeTopicRef = topicRefs[activeSection];
                if (activeTopicRef?.current) {
                    const containerRect = container.getBoundingClientRect();
                    const topicRect = activeTopicRef.current.getBoundingClientRect();
                    const leftPosition = topicRect.left - containerRect.left;
                    setScrollProgress(leftPosition);
                }
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [activeSection]);

    return (
        <div className="blog" id="core-ux-design">
            <div className="blog-container">
                <div className="blog-topics-container">
                    <div className="blog-topics">
                        <div ref={topicsContainerRef}>
                            <div>
                                <div className="blog-topic" ref={userCenteredTopicRef}>
                                    <div className="circle"></div>
                                    <h1
                                        className={`${activeSection === 0 ? "active" : ""}`}
                                        onClick={() => userCenteredSectionRef.current?.scrollIntoView({ behavior: "smooth" })}
                                    >
                                        User-Centered Design
                                    </h1>
                                </div>
                                <div className="blog-topic" ref={clarityTopicRef}>
                                    <div className="circle"></div>
                                    <h1
                                        className={`${activeSection === 1 ? "active" : ""}`}
                                        onClick={() => claritySectionRef.current?.scrollIntoView({ behavior: "smooth" })}
                                    >
                                        Clarity & Simplicity
                                    </h1>
                                </div>
                                <div className="blog-topic" ref={consistencyTopicRef}>
                                    <div className="circle"></div>
                                    <h1
                                        className={`${activeSection === 2 ? "active" : ""}`}
                                        onClick={() => consistencySectionRef.current?.scrollIntoView({ behavior: "smooth" })}
                                    >
                                        Consistency
                                    </h1>
                                </div>
                            </div>

                            <div className="blog-topics-social-links">
                                <svg xmlns="http://www.w3.org/2000/svg" width={36} height={36} viewBox="0 0 36 36" fill="none">
                                    <circle cx={18} cy={18} r="17.5" stroke="#E9E9E9" />
                                    <path d="M24.0804 10C24.5507 10 25.0178 10 25.4881 10C25.4721 10.0219 25.4561 10.0406 25.4401 10.0625C23.5109 12.2531 21.5817 14.4438 19.6525 16.6313C19.5597 16.7375 19.5437 16.8031 19.6301 16.9281C21.4665 19.5281 23.2965 22.1313 25.1266 24.7344C25.4209 25.1531 25.7313 25.5625 26 26C24.4099 26 22.823 26 21.233 26C21.2042 25.9563 21.1786 25.9094 21.1498 25.8687C19.758 23.8906 18.3695 21.9156 16.9778 19.9375C16.8754 19.7906 16.8722 19.7875 16.7538 19.925C15.6053 21.2281 14.4599 22.5281 13.3113 23.8313C12.6747 24.5531 12.0444 25.2781 11.4077 26C10.9374 26 10.4703 26 10 26C10.0384 25.95 10.0736 25.9 10.1152 25.8531C12.118 23.5812 14.1176 21.3063 16.1236 19.0375C16.226 18.925 16.2164 18.8563 16.1364 18.7406C14.1272 15.8906 12.1212 13.0406 10.1152 10.1875C10.0736 10.1281 10.0192 10.075 10 10.0031C11.5901 10.0031 13.177 10.0031 14.767 10.0031C14.7766 10.0219 14.7862 10.0406 14.7958 10.0563C16.1364 11.9656 17.4801 13.8719 18.8206 15.7844C18.9102 15.9125 18.9454 15.8281 18.9966 15.7688C20.6955 13.8438 22.3879 11.9219 24.0804 10ZM11.9196 11.0375C11.9644 11.1062 11.9932 11.1562 12.0252 11.2C12.2747 11.55 12.5275 11.9 12.777 12.25C15.7908 16.4594 18.8014 20.6688 21.812 24.8781C21.8824 24.975 21.9528 25.0094 22.0712 25.0094C22.6727 25.0031 23.2773 25.0063 23.8788 25.0063C23.9332 25.0063 23.994 25.0281 24.0548 24.975C23.882 24.7313 23.7093 24.4875 23.5333 24.2469C20.4203 19.8969 17.3073 15.5469 14.1944 11.1969C14.1144 11.0844 14.0312 11.0313 13.8872 11.0344C13.3753 11.0438 12.8634 11.0375 12.3515 11.0375C12.2172 11.0375 12.0828 11.0375 11.9196 11.0375Z" fill="#0E0E23" />
                                </svg>

                                <svg xmlns="http://www.w3.org/2000/svg" width={36} height={36} viewBox="0 0 36 36" fill="none">
                                    <path d="M22 11C22 11.8589 22 12.719 22 13.5779C21.501 13.5741 20.9963 13.5309 20.5029 13.5766C19.6717 13.6516 19.2385 14.0531 19.1362 14.8052C19.0843 15.1889 19.1067 15.5802 19.0801 15.9677C19.0661 16.1723 19.1572 16.2307 19.3745 16.2282C20.1427 16.2193 20.9122 16.2282 21.6804 16.2218C21.8851 16.2206 21.9481 16.2701 21.9019 16.4645C21.7393 17.1506 21.5865 17.8392 21.4463 18.5304C21.4085 18.7146 21.3215 18.768 21.1253 18.7641C20.5716 18.7552 20.0165 18.7603 19.4628 18.7603C19.0829 18.7603 19.0815 18.7603 19.0815 19.1072C19.0815 20.9787 19.0773 22.8501 19.0857 24.7229C19.0871 24.9363 19.0184 25.0037 18.7815 24.9998C18.075 24.9871 17.3685 24.996 16.6635 24.996C16.2808 24.996 16.2808 24.996 16.2808 24.653C16.2808 22.7815 16.2766 20.91 16.2864 19.0373C16.2878 18.8086 16.2093 18.7502 15.9682 18.7552C15.4061 18.7692 14.8411 18.7527 14.279 18.7641C14.066 18.768 13.9973 18.7006 14.0001 18.5126C14.0085 17.8316 14.0085 17.1519 14.0001 16.4709C13.9973 16.2841 14.0632 16.2142 14.279 16.2193C14.8594 16.2307 15.4411 16.2168 16.0214 16.2269C16.2163 16.2295 16.2934 16.1812 16.2836 15.9957C16.2682 15.6806 16.2569 15.3617 16.285 15.0479C16.3368 14.4698 16.3509 13.879 16.5022 13.32C16.8597 12.005 17.9965 10.9924 19.802 11.014C20.5337 11.0241 21.2669 11.0051 22 11Z" fill="#050320" />
                                    <circle cx={18} cy={18} r="17.5" stroke="#E9E9E9" />
                                </svg>

                                <svg xmlns="http://www.w3.org/2000/svg" width={36} height={36} viewBox="0 0 36 36" fill="none">
                                    <circle cx={18} cy={18} r="17.5" stroke="#E9E9E9" />
                                    <path d="M24.0003 23.9999C23.1411 23.9999 22.2868 23.9999 21.4239 23.9999C21.4239 23.9751 21.4239 23.9515 21.4239 23.9279C21.4239 22.4832 21.4251 21.0398 21.4227 19.5951C21.4227 19.287 21.3904 18.9814 21.2886 18.687C21.183 18.3852 21.0142 18.133 20.7211 17.9839C20.4616 17.8523 20.1835 17.8249 19.9004 17.8672C19.4212 17.9392 19.0971 18.2212 18.8686 18.6348C18.7358 18.8771 18.7035 19.1379 18.7023 19.4063C18.701 20.9118 18.7023 22.4186 18.7023 23.9242C18.7023 23.9478 18.7023 23.9726 18.7023 23.9987C17.8431 23.9987 16.9888 23.9987 16.1309 23.9987C16.1309 21.3279 16.1309 18.6597 16.1309 15.9877C16.9863 15.9877 17.8406 15.9877 18.701 15.9877C18.701 16.3541 18.701 16.7206 18.701 17.0871C18.7047 17.0883 18.7085 17.0895 18.7122 17.0908C18.7544 17.0324 18.7966 16.9753 18.8388 16.9169C19.0859 16.5827 19.374 16.2945 19.7427 16.097C20.1065 15.902 20.4989 15.8138 20.9086 15.8014C21.3419 15.7877 21.7653 15.8411 22.1713 15.9976C22.7648 16.2262 23.2118 16.625 23.521 17.179C23.7668 17.6212 23.9009 18.0982 23.9531 18.5988C23.9804 18.8647 23.9978 19.133 23.9978 19.4013C24.0015 20.9093 24.0003 22.4174 24.0003 23.9254C24.0003 23.949 24.0003 23.9739 24.0003 23.9999Z" fill="#050320" />
                                    <path d="M12.0078 15.9893C12.8645 15.9893 13.7188 15.9893 14.5767 15.9893C14.5767 18.6588 14.5767 21.3283 14.5767 24.0003C13.72 24.0003 12.8658 24.0003 12.0078 24.0003C12.0078 21.3295 12.0078 18.6612 12.0078 15.9893Z" fill="#050320" />
                                    <path d="M13.3976 14.7681C13.0127 14.7643 12.6874 14.6637 12.4192 14.4103C12.2032 14.2066 12.0777 13.9532 12.0268 13.6625C11.9685 13.3321 12.002 13.0103 12.1597 12.711C12.3795 12.2911 12.7383 12.07 13.2027 12.0128C13.4423 11.983 13.6782 12.0042 13.9054 12.0861C14.2419 12.2079 14.4865 12.4315 14.6305 12.7582C14.8106 13.1656 14.8255 13.5805 14.6429 13.9892C14.4331 14.4575 14.0544 14.696 13.554 14.7556C13.4944 14.7643 13.4348 14.7656 13.3976 14.7681Z" fill="#050320" />
                                </svg>
                            </div>
                        </div>
                        <div className="blog-topics-border">
                            <div className="line" style={isMobile ? { left: `${scrollProgress}px` } : { top: `${scrollProgress}px` }}></div>
                        </div>
                    </div>
                </div>

                <div className="blog-sections">
                    <div className="blog-section" id="user-centered" ref={(el) => { userCenteredRef(el); userCenteredSectionRef.current = el; }}>
                        <h1>Core UX Design</h1>
                        <img src="/assets/images/blog-pages/core-ux-design/blog-image-1.png" alt="Core UX Design Image" />
                        <div className="blog-section-details">
                            <p>
                                <span className="bold">
                                    1. User-Centered Design
                                </span>
                                <br />
                                Put Users First:
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Design for your users, not yourself
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Understand user needs, goals, and pain points
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Base decisions on user research, not assumptions
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Test with real users throughout process
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Empathy is fundamental
                                </span>
                                <br />
                                User Research Methods:
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; User interviews
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Surveys and questionnaires
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Usability testing
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; User personas
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Journey mapping
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Analytics
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; A/B testing
                                </span>
                                <br />
                                Key Concept: "You are not your user" - Your preferences, knowledge, and behavior differ from your target users.
                            </p>
                        </div>
                    </div>
                    <br />
                    <div className="blog-section" id="clarity" ref={(el) => { clarityRef(el); claritySectionRef.current = el; }}>
                        <div className="blog-section-details">
                            <p>
                                <span className="bold">
                                    2. Clarity & Simplicity
                                </span>
                                <br />
                                Make It Obvious:
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Clear, understandable interface
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Obvious navigation and actions
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Self-explanatory functionality
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Minimal cognitive load
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Remove unnecessary elements
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; One primary action per screen
                                </span>
                                <br />
                                Simplicity Guidelines:
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Use familiar patterns
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Clear labels and instructions
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Obvious clickable elements
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Predictable behavior
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Clean visual hierarchy
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; White space for breathing room
                                </span>
                                <br />
                                Quote: "Don't make me think" - Steve Krug
                            </p>
                        </div>
                    </div>
                    <br />
                    <div className="blog-section" id="consistency" ref={(el) => { consistencyRef(el); consistencySectionRef.current = el; }}>
                        <div className="blog-section-details">
                            <p>
                                <span className="bold">
                                    3. Consistency
                                </span>
                                <br />
                                Create Predictable Experiences:
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Consistent visual design
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Consistent interaction patterns
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Consistent terminology
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Consistent navigation
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Consistent feedback
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Follow platform conventions
                                </span>
                                <br />
                                Types of Consistency:
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Internal: Within your product
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; External: With platform standards (iOS, Android, Web)
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Visual: Colors, typography, spacing
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Functional: How things work
                                </span>
                                <br />
                                &nbsp;&nbsp;<span>
                                    &middot;&nbsp; Content: Tone, voice, messaging
                                </span>
                                <br />
                                Benefit: Users learn once, apply everywhere
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Blogs Slider Section */}
            <BlogsSlider />
        </div>
    )
}

export default CoreUxDesign
